// frontend/src/starters/api.ts
//
// W2-B/F-01 „Sofortstart" — Transport und Auswahl fuer die beiden Tueren.
//
//   Abenteuer: GET /api/discover/adventures/starters
//              → { items: AdventureTemplateItem[], source }
//              Der SERVER waehlt (Starter-Vorlagen `starter_…`, Rueckfall auf
//              die bestbewerteten freigegebenen Vorlagen; Sprache und USK aus
//              dem Konto). Gespielt wird ueber POST …/:key/play (0 Coins, kein
//              Modellaufruf) — derselbe Weg wie unter „Entdecken".
//   Begleiter: GET /api/discover/characters/starters
//              → { items: DiscoverItem[], source: 'katalog' | 'none' }
//              Auch hier waehlt der SERVER (Review M4): alle freigegebenen
//              Katalog-Begleiter mit gruenem Bild, Welten gestreut, Stufe und
//              Sprache aus dem Konto. Frueher nahm der Client drei aus den
//              ersten 48 Listeneintraegen — mit wachsenden Nutzer-
//              Publikationen lief das still leer.
//              Sofort-Chat = POST …/:id/adopt → /character-chat/:id.
//
// Wie alle Entdecken-Daten laufen die Vorschlaege NUR ueber react-query —
// nie in Dexie (Katalogdaten, kein Nutzerbestand).

import { ApiError, apiRequest } from '../api/client';
import { normalizeDiscoverItem, type DiscoverItem } from '../api/discover';
import { normalizeAdventureItem, type AdventureTemplateItem } from '../pages/discover/adventures/api';

export type StarterSource = 'starter' | 'fallback' | 'mixed' | 'none';

export type AdventureStarter = AdventureTemplateItem & { chips?: string[] };

export interface AdventureStartersResponse {
  items: AdventureStarter[];
  source: StarterSource;
}

/** So viele Vorschlaege zeigt jede Tuer (E-06: drei). */
export const STARTER_COUNT = 3;

const SOURCES: readonly StarterSource[] = ['starter', 'fallback', 'mixed', 'none'];

export async function getAdventureStarters(limit = STARTER_COUNT): Promise<AdventureStartersResponse> {
  let response: { items?: unknown; source?: unknown } | unknown[];
  try {
    response = await apiRequest('/api/discover/starters?door=adventure');
  } catch (error) {
    // Alte Server kennen den Vertragspfad noch nicht. Netzwerk-/Authfehler
    // bleiben dagegen sichtbar und lösen keinen zweiten Startweg aus.
    if (!(error instanceof ApiError) || ![404, 405].includes(error.status)) throw error;
    response = await apiRequest(`/api/discover/adventures/starters?limit=${encodeURIComponent(String(limit))}`);
  }
  const rows = Array.isArray(response) ? response : response?.items;
  const items = (Array.isArray(rows) ? rows : [])
    .map((raw): AdventureStarter | null => {
      const item = normalizeAdventureItem(raw);
      if (!item) return null;
      const chips = Array.isArray(raw.chips)
        ? raw.chips.filter((value: unknown): value is string => typeof value === 'string' && !!value.trim()).slice(0, 3)
        : [];
      return { ...item, chips };
    })
    .filter((row): row is AdventureStarter => row !== null)
    .slice(0, limit);
  const responseSource = Array.isArray(response) ? undefined : response?.source;
  const source = SOURCES.includes(responseSource as StarterSource)
    ? (responseSource as StarterSource)
    : items.length > 0 ? 'starter' : 'none';
  return { items, source };
}

/**
 * Drei Katalogfiguren fuer den Sofort-Chat: nur Lorath-Vorlagen mit Bild,
 * moeglichst aus verschiedenen Welten, sonst in Listenreihenfolge. Der Server
 * waehlt bereits genau so aus; hier bleibt es die letzte Schranke gegen eine
 * unerwartete Antwort (fremde Zeile, fehlendes Bild). Rein und testbar — die
 * USK-Grenze hat der Server angewendet.
 */
export function pickCompanionStarters(items: readonly DiscoverItem[], limit = STARTER_COUNT): DiscoverItem[] {
  const candidates = items.filter((item) => item.origin === 'katalog' && item.portraits.length > 0);
  const picked: DiscoverItem[] = [];
  const settings = new Set<string>();
  for (const item of candidates) {
    if (picked.length >= limit) break;
    if (settings.has(item.setting)) continue;
    picked.push(item);
    settings.add(item.setting);
  }
  for (const item of candidates) {
    if (picked.length >= limit) break;
    if (picked.includes(item)) continue;
    picked.push(item);
  }
  return picked;
}

export async function getCompanionStarters(limit = STARTER_COUNT): Promise<DiscoverItem[]> {
  const response = await apiRequest<{ items?: unknown }>(
    `/api/discover/characters/starters?limit=${encodeURIComponent(String(limit))}`,
  );
  const items = (Array.isArray(response?.items) ? response.items : [])
    .map(normalizeDiscoverItem)
    .filter((row): row is DiscoverItem => row !== null);
  return pickCompanionStarters(items, limit);
}

/**
 * react-query-Schluessel — eigene Wurzel, damit Entdecken-Caches unberuehrt
 * bleiben. Die Konto-Id gehoert hinein: Sprache und USK haengen am Konto, ein
 * Kontowechsel im selben Tab darf nie die Vorschlaege des Vorgaengers zeigen.
 */
export const starterQueryKeys = {
  all: ['starters'] as const,
  adventures: (userId: number | null, limit: number) => ['starters', 'adventures', userId, limit] as const,
  companions: (userId: number | null, limit: number) => ['starters', 'companions', userId, limit] as const,
};
